import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    RiTwitterXFill,
    RiLinkedinFill,
    RiMailLine,
    RiPlantLine,
    RiLineChartLine,
    RiTeamLine,
    RiMicroscopeLine,
    RiGlobalLine,
    RiLightbulbFlashLine
} from 'react-icons/ri';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config';

const TeamMember = ({ member, index }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.8, ease: "easeOut" }}
            className="group relative bg-white/40 backdrop-blur-xl rounded-[2.5rem] p-8 border border-white/60 hover:border-emerald-200/50 shadow-[0_8px_30px_rgba(15,23,42,0.03)] hover:shadow-[0_20px_50px_rgba(16,185,129,0.08)] transition-all duration-500 flex flex-col items-center text-center overflow-hidden"
        >
            {/* HUD Decorative Elements */}
            <div className="absolute top-4 left-6 flex gap-1.5 opacity-20 group-hover:opacity-40 transition-opacity">
                <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
                <div className="w-1 h-1 bg-slate-400 rounded-full" />
                <div className="w-8 h-[1px] bg-slate-200 mt-0.5" />
            </div>
            <div className="absolute top-4 right-6 text-[8px] font-black text-slate-300 uppercase tracking-widest font-sans">
                Personnel ID: 00{index + 1}
            </div>

            {/* Circular Image with Dual Accent Rings */}
            <div className="relative mb-8 pt-4">
                <div className="absolute inset-0 bg-emerald-100/30 rounded-full blur-2xl scale-0 group-hover:scale-100 transition-transform duration-700" />
                <div className="relative w-32 h-32 p-1.5 bg-gradient-to-br from-emerald-100 to-slate-100 rounded-full shadow-inner ring-1 ring-white/50">
                    <div className="w-full h-full rounded-full overflow-hidden border-2 border-white shadow-xl">
                        <img
                            src={member.image}
                            alt={member.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-115"
                        />
                    </div>
                    {/* Floating Tech Tag */}
                    <div className="absolute -bottom-2 -right-2 bg-slate-900 text-white text-[8px] font-black px-2 py-1 rounded-md shadow-lg border border-slate-700 uppercase tracking-tighter scale-0 group-hover:scale-100 transition-transform duration-300">
                        Verified
                    </div>
                </div>
            </div>

            {/* Content Tier */}
            <div className="relative z-10 w-full mb-6">
                <div className="h-[1px] w-12 bg-emerald-100 mx-auto mb-4 group-hover:w-20 transition-all duration-500" />
                <h3 className="text-2xl font-bold text-slate-900 mb-1 font-display tracking-tight group-hover:text-emerald-950 transition-colors">
                    {member.name}
                </h3>
                <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-[0.25em] font-sans">
                        {member.role}
                    </p>
                </div>

                <div className="relative">
                    <div className="absolute -left-2 top-0 text-emerald-100 opacity-50"><RiMailLine size={12} /></div>
                    <p className="text-slate-500 text-sm leading-relaxed px-2 font-sans italic opacity-80 group-hover:opacity-100 transition-opacity line-clamp-3">
                        "{member.quote || member.bio}"
                    </p>
                </div>
            </div>

            {/* Social Matrix */}
            <div className="flex items-center gap-3 p-1.5 bg-slate-50/50 rounded-2xl border border-slate-100/50 mt-auto opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <button className="p-2.5 hover:bg-white hover:text-slate-900 text-slate-400 rounded-xl transition-all shadow-sm flex items-center justify-center"><RiTwitterXFill size={16} /></button>
                <div className="w-[1px] h-4 bg-slate-200" />
                <button className="p-2.5 hover:bg-white hover:text-blue-600 text-slate-400 rounded-xl transition-all shadow-sm flex items-center justify-center"><RiLinkedinFill size={18} /></button>
                <div className="w-[1px] h-4 bg-slate-200" />
                <button className="p-2.5 hover:bg-white hover:text-emerald-600 text-slate-400 rounded-xl transition-all shadow-sm flex items-center justify-center"><RiMailLine size={16} /></button>
            </div>

            {/* Background Texture Overlay */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(16,185,129,0.03),transparent)] pointer-events-none" />
        </motion.div>
    );
};

const ValuePill = ({ icon: Icon, text, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        whileHover={{ scale: 1.05, y: -5 }}
        viewport={{ once: true }}
        transition={{ delay, duration: 0.5 }}
        className="group relative flex items-center gap-4 px-6 py-4 bg-white/60 backdrop-blur-xl rounded-2xl shadow-[0_8px_30px_rgba(15,23,42,0.04)] border border-white/80 hover:border-emerald-200/50 transition-all duration-300 cursor-default"
    >
        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-slate-50 rounded-xl group-hover:bg-emerald-50 transition-colors duration-300 shadow-inner relative overflow-hidden">
            <motion.div
                animate={{
                    y: [0, -2, 0],
                }}
                transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: Math.random() * 2
                }}
                className="text-emerald-600 relative z-10"
            >
                <Icon size={20} className="group-hover:scale-125 transition-transform duration-300" />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-tr from-emerald-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="flex flex-col">
            <span className="text-[10px] font-black text-emerald-600/40 uppercase tracking-[0.2em] mb-0.5 font-sans">Strategic Core</span>
            <span className="text-sm font-bold text-slate-800 tracking-tight font-display">{text}</span>
        </div>

        {/* Decorative corner glow */}
        <div className="absolute top-0 right-0 w-12 h-12 bg-emerald-400/5 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </motion.div>
);

const About = () => {
    const [team, setTeam] = useState([]);
    const [content, setContent] = useState({
        hero: {
            title: "It's hard to save the planet alone. That's why COE was born.",
            description: "We started with a simple idea: help researchers and communities focus on their impact, restoration, and dreams, not formatting data. We trusted that by building the right tools, we could remove the barriers and bring out the best in every ecosystem's story."
        },
        values: {
            title: "Our value.",
            subtitle: "Less \"doom\", more \"bloom\"."
        },
        quote: {
            text: "We built COE to remove the friction from conservation efforts. It's not just a database, it's a way to help people dream bigger and move forward faster.",
            author: "Dr. Elena Rivers, CEO"
        }
    });

    const fetchData = async () => {
        try {
            // Fetch Team
            const teamRes = await fetch(`${API_BASE_URL}/team/read.php`);
            const teamData = await teamRes.json();
            if (teamRes.ok && teamData.records && teamData.records.length > 0) {
                setTeam(teamData.records);
            } else {
                // Fallback static team if DB is empty
                setTeam([
                    {
                        name: "Dr. Elena Rivers",
                        role: "Scientific Director",
                        image_url: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=1976&auto=format&fit=crop",
                        bio: "Nature doesn't need us. We need nature.",
                    },
                    {
                        name: "Marcus Thorne",
                        role: "Conservation Lead",
                        image_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1887&auto=format&fit=crop",
                        bio: "Every tree planted is a promise to the future.",
                    }
                ]);
            }

            // Fetch Content Sections
            const sections = {
                'about_hero': 'hero',
                'about_values': 'values',
                'about_quote': 'quote'
            };

            const updatedContent = { ...content };
            for (const [key, stateKey] of Object.entries(sections)) {
                const res = await fetch(`${API_BASE_URL}/content/read.php?key=${key}`);
                const data = await res.json();
                if (res.ok && data.content) {
                    updatedContent[stateKey] = data.content;
                }
            }
            setContent(updatedContent);

        } catch (error) {
            console.error('Error fetching about data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <div className="bg-[#FAFAFA] min-h-screen pt-32 pb-20 font-sans selection:bg-emerald-100 selection:text-emerald-900">

            {/* Header Section */}
            <div className="container mx-auto px-6 max-w-4xl text-center mb-32">
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-3xl sm:text-4xl md:text-6xl font-medium text-slate-900 leading-tight mb-8 font-display"
                >
                    {content.hero.title}
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className="text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto font-sans"
                >
                    {content.hero.description}
                </motion.p>
            </div>

            {/* Values Section */}
            <div className="container mx-auto px-6 mb-40 text-center">
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="mb-12"
                >
                    <h2 className="text-3xl font-bold text-slate-900 mb-2 font-display">{content.values.title}</h2>
                    <p className="text-xl text-slate-400 font-light font-sans">{content.values.subtitle}</p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    <ValuePill icon={RiPlantLine} text="Eco-First Approach" delay={0.1} />
                    <ValuePill icon={RiLineChartLine} text="Smart Data Analysis" delay={0.2} />
                    <ValuePill icon={RiTeamLine} text="Community Led" delay={0.3} />
                    <ValuePill icon={RiMicroscopeLine} text="Scientific Rigor" delay={0.4} />
                    <ValuePill icon={RiGlobalLine} text="Global Impact" delay={0.5} />
                    <ValuePill icon={RiLightbulbFlashLine} text="Innovation" delay={0.6} />
                </div>
            </div>

            {/* Team Section */}
            <div className="container mx-auto px-6 max-w-7xl mb-40">
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 font-display">Let's Meet Our Team</h2>
                    <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed">
                        Meet the passionate individuals driving our mission forward. From field researchers to tech innovators, we are united by a shared vision for a sustainable future.
                    </p>

                    {/* Toggle Buttons */}
                    <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm">
                        <button className="px-8 py-2.5 rounded-full text-sm font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                            About Us
                        </button>
                        <button className="px-8 py-2.5 rounded-full text-sm font-bold text-white bg-emerald-600 shadow-md">
                            Members
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {team.map((member, i) => (
                        <TeamMember key={i} member={{ ...member, image: member.image_url }} index={i} />
                    ))}
                </div>
            </div>

            {/* Bottom Narrative */}
            <div className="container mx-auto px-6 max-w-3xl text-center mb-32">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="p-12 rounded-[40px] bg-white border border-slate-100 shadow-xl shadow-slate-200/50"
                >
                    <p className="text-xl md:text-2xl text-slate-700 font-light leading-relaxed mb-8">
                        "{content.quote.text}"
                    </p>
                    <div className="w-16 h-1 bg-slate-100 mx-auto rounded-full mb-8"></div>
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                        Behind every species is a real story. That's why we created this platform, to make sure those stories shine.
                    </p>
                    <div className="mt-8 text-xs text-slate-300 font-serif italic">
                        — {content.quote.author}
                    </div>
                </motion.div>
            </div>

            <Footer />
        </div>
    );
};

export default About;
